import { checkPresensiMasuk, checkPresensiPulang, getWibDateString, getJamAkhirToleransi, isDalamBatasUdzur, UdzurMasuk } from "../../utils/presensiLogic";
import { useMasaPenyesuaian } from "../../hooks/useMasaPenyesuaian";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ShiftConfig } from '../../hooks/useUserShift';
import { Home, Newspaper, BarChart3, User, MapPin, Loader2, ArrowRight } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useUserAccess } from '../../context/UserAccessContext';
import { useUserShift } from '../../hooks/useUserShift';
import { usePresensiPegawai } from '../../hooks/usePresensiPegawai';
import { usePresensiSubmit } from '../../hooks/usePresensiSubmit';
import { useCekIzinTerlambat } from '../../hooks/useCekIzinTerlambat';
import { useCekIzinPulangCepat } from '../../hooks/useCekIzinPulangCepat';
import { getSupabaseToken, isSupabaseTokenReady, ensureSupabaseTokenFresh } from '../../lib/supabase';
import { PortalGuruModalPilihanShift } from './presensi/PortalGuruModalPilihanShift';
import { PortalGuruModalPilihanLokasi, LocationData } from './presensi/PortalGuruModalPilihanLokasi';
import { PortalGuruModalPresensiTepat } from './presensi/PortalGuruModalPresensiTepat';
import { PortalGuruModalPresensiTerlambat } from './presensi/PortalGuruModalPresensiTerlambat';
import { PortalGuruModalError } from './presensi/PortalGuruModalError';
import { PortalGuruModalKonfirmasiPulang } from './presensi/PortalGuruModalKonfirmasiPulang';
import { PortalGuruModalPulangCepat } from './presensi/PortalGuruModalPulangCepat';
import { PortalGuruModalTidakPresensiPulang } from './presensi/PortalGuruModalTidakPresensiPulang';
import { PortalGuruModalShiftSelesai } from './presensi/PortalGuruModalShiftSelesai';
import { PortalGuruModalMetodePresensi } from './presensi/PortalGuruModalMetodePresensi';
import { PortalGuruScannerPresensi } from './presensi/PortalGuruScannerPresensi';
import { PortalGuruModalRefreshLokasi } from './presensi/PortalGuruModalRefreshLokasi';
import { PortalGuruModalAutoClosedInfo } from './presensi/PortalGuruModalAutoClosedInfo';

import { doc, getDoc, getDocFromCache } from 'firebase/firestore';
import { startDiag, diagStep, finishDiag, formatDiag, isDiagActive, DIAG_EVENT } from '../../utils/presensiDiagnostics';
import { db } from '../../firebase';

interface PortalGuruBottomNavProps {
  activeTab: 'home' | 'berita' | 'kinerja' | 'profil';
}

export const PortalGuruBottomNav: React.FC<PortalGuruBottomNavProps> = ({ activeTab }) => {
  const navigate = useNavigate();
  const { authUser } = useAuth();
  // Akun layanan (tanpa data pegawai) tidak punya presensi pegawai dan kinerja.
  const { isAkunLayanan } = useUserAccess();
  const { shiftData, loading: shiftLoading } = useUserShift(authUser);
  const { activeSession, dailyRecords, autoClosedSessionInfo, clearAutoClosedSessionInfo, calculateDistance, isPointInPolygon } = usePresensiPegawai();
  // Submit dipisah ke hook khusus supaya logika refetch (fetchTodayAttendance)
  // tidak dijalankan dua kali — dulu submitAttendance milik usePresensiPegawai
  // memanggil fetchTodayAttendance lalu event `presensi_updated` memicunya lagi.
  // Sekarang cukup satu kali lewat event, sehingga 1× refetch (bukan 2×).
  const { submitting: presensiSubmitting, submitAttendance } = usePresensiSubmit(() => {
    window.dispatchEvent(new Event('presensi_updated'));
  });
  const { cekIzinTerlambat } = useCekIzinTerlambat();
  // Masa penyesuaian aplikasi: modal terlambat menawarkan udzur "Lupa" / "Aplikasi Error".
  const { berlaku: masaPenyesuaian, refresh: muatMasaPenyesuaian } = useMasaPenyesuaian();
  // Shift yang dipakai saat modal terlambat dibuka (untuk menghitung jam akhir toleransi).
  const [lateConfig, setLateConfig] = useState<ShiftConfig | null>(null);
  const { cekIzinPulangCepat } = useCekIzinPulangCepat();

  // Background token warm-up (non-blocking)
  useEffect(() => {
      if (!isSupabaseTokenReady()) {
          getSupabaseToken().catch(e => console.warn("Background token pre-warm in nav failed:", e));
      }
  }, []);

  // Modal States
  const [activeModal, setActiveModal] = useState<'shift' | 'location' | 'success' | 'late' | 'early' | 'missed_checkout' | 'confirm_pulang' | 'shift_completed' | 'error' | 'loading' | 'refresh_lokasi' | 'method' | 'scanner' | 'auto_closed_info' | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successData, setSuccessData] = useState<{ type: 'masuk' | 'pulang', status: 'tepat_waktu' | 'terlambat' | 'pulang_cepat' | 'lembur' | 'tidak_presensi_pulang' } | null>(null);

  // Data States for Flow
  const [selectedShift, setSelectedShift] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);
  const [tempCoords, setTempCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [tempDistance, setTempDistance] = useState<number>(0);
  const [currentAction, setCurrentAction] = useState<'masuk' | 'pulang'>('masuk');

  // Temp state for Pulang status
  const [pulangStatus, setPulangStatus] = useState<'pulang_cepat' | 'tepat_waktu' | 'lembur' | 'tidak_presensi_pulang'>('tepat_waktu');

  // Track if we are forcing a missed checkout input
  const [pendingMissedCheckout, setPendingMissedCheckout] = useState<{ shiftName: string; jam_masuk: string } | null>(null);

  // Guard klik-ganda SYNKRON — state React butuh 1 render untuk ter-update,
  // sedangkan di jaringan lambat user bisa menekan tombol submit dua kali.
  const submitInFlightRef = useRef(false);

  // ---- Diagnosis kecepatan presensi (lihat utils/presensiDiagnostics.ts) ----
  const [diagLabel, setDiagLabel] = useState<string | null>(null);
  const [diagElapsed, setDiagElapsed] = useState(0);
  const [diagSummary, setDiagSummary] = useState('');
  const diagStartRef = useRef<number>(0);

  useEffect(() => {
      const onDiag = (e: Event) => {
          const detail = (e as CustomEvent).detail as { label: string | null } | undefined;
          setDiagLabel(detail?.label ?? null);
      };
      window.addEventListener(DIAG_EVENT, onDiag);
      return () => window.removeEventListener(DIAG_EVENT, onDiag);
  }, []);

  // Detik berjalan di modal loading, supaya guru bisa melaporkan lamanya.
  useEffect(() => {
      if (activeModal !== 'loading') return;
      diagStartRef.current = Date.now();
      setDiagElapsed(0);
      const iv = setInterval(() => setDiagElapsed(Date.now() - diagStartRef.current), 500);
      return () => clearInterval(iv);
  }, [activeModal]);

  /** Tutup pencatatan diagnosis & simpan ringkasannya untuk modal hasil. */
  const endDiag = () => {
      const trace = finishDiag();
      setDiagSummary(trace ? formatDiag(trace) : '');
  };

  // ---- Cek izin (terlambat / pulang cepat) dijalankan LEBIH AWAL ----
  // Dimulai saat pegawai memilih shift / konfirmasi pulang, sehingga sudah
  // selesai di latar belakang ketika dibutuhkan (menghemat 1 permintaan
  // berurutan di jaringan lambat).
  const izinPrefetchRef = useRef<{ key: string; promise: Promise<any> } | null>(null);
  // `refresh` = mulai alur presensi baru → selalu ambil data izin terbaru
  // (izin bisa saja baru disetujui setelah percobaan sebelumnya).
  const getIzin = (jenis: 'terlambat' | 'pulang_cepat', refresh = false) => {
      if (!authUser?.uid || !authUser?.email) return Promise.resolve(null);
      const dateStr = getWibDateString(new Date());
      const key = `${jenis}:${authUser.uid}:${dateStr}`;
      if (refresh || izinPrefetchRef.current?.key !== key) {
          const promise = (jenis === 'terlambat'
              ? cekIzinTerlambat(authUser.uid, authUser.email, dateStr)
              : cekIzinPulangCepat(authUser.uid, authUser.email, dateStr)
          ).catch(() => null);
          izinPrefetchRef.current = { key, promise };
      }
      return izinPrefetchRef.current.promise;
  };

  // Trigger auto-closed notification modal when client-side or server auto-close detects an unclosed session closed
  useEffect(() => {
    if (autoClosedSessionInfo) {
        setSelectedShift(autoClosedSessionInfo.shiftName);
        setActiveModal('auto_closed_info');
    }
  }, [autoClosedSessionInfo]);

  // If there's a pending missed checkout reason to input, we intercept the normal flow
  useEffect(() => {
    if (pendingMissedCheckout) {
        setErrorMessage(`Perhatian: Anda lupa melakukan presensi pulang pada sesi ${pendingMissedCheckout.shiftName} sebelumnya. Sistem telah mencatat status Anda sebagai Tidak Presensi Pulang.`);
        setCurrentAction('pulang');
        setPulangStatus('tidak_presensi_pulang');
        setActiveModal('missed_checkout');
    }
  }, [pendingMissedCheckout]);


  // Determine App Readiness (non-blocking for tokens since getSupabaseToken handles on-demand)
  const isAppReady = !shiftLoading;

  const handleLocationClick = () => {
    if (!isAppReady) return; // Fail-safe

    // Mulai siapkan token Supabase SEKARANG (latar belakang). Bila harus diminta
    // ke Cloud Function, prosesnya berjalan bersamaan dengan memilih shift &
    // mencari lokasi GPS, sehingga sudah siap saat presensi disimpan.
    ensureSupabaseTokenFresh().catch(() => null);

    if (pendingMissedCheckout) {
        setActiveModal('missed_checkout');
        return;
    }

    // Determine Action: If active session exists, default to Pulang. Else Masuk.
    let action: 'masuk' | 'pulang' = activeSession ? 'pulang' : 'masuk';

    setCurrentAction(action);

    // 2. Check Shift
    if (!shiftData.todayConfig && !activeSession) {
        setErrorMessage('Jadwal shift Anda hari ini tidak ditemukan atau libur.');
        setActiveModal('error');
        return;
    }

    // 3. Flow
    if (action === 'pulang') {
        setActiveModal('confirm_pulang');
    } else {
        setActiveModal('shift');
    }
  };

  const handleConfirmPulang = () => {
      getIzin('pulang_cepat', true); // prefetch di latar belakang
      setActiveModal('method');
  };

  const handleMethodSelect = (method: 'gps' | 'qr') => {
      if (method === 'gps') {
          setActiveModal('location');
      } else {
          setActiveModal('scanner');
      }
  };

  const handleQRSuccess = async (lokasiId: string) => {
      setActiveModal('loading');
      startDiag();
      try {
          // Data lokasi: utamakan cache lokal Firestore (instan); hanya ke
          // server bila belum pernah tersimpan di HP.
          const docRef = doc(db, 'lokasi_presensi', lokasiId);
          const docSnap = await diagStep('Data lokasi', async () => {
              try {
                  const cached = await getDocFromCache(docRef);
                  if (cached.exists()) return cached;
              } catch { /* belum ada di cache */ }
              return getDoc(docRef);
          });
          let foundLoc: any = null;
          if (docSnap.exists()) {
              foundLoc = { id: docSnap.id, ...docSnap.data() };
          }

          if (!foundLoc) {
              setErrorMessage('Data lokasi QR Code tidak ditemukan.');
              setActiveModal('error');
              return;
          }

          // Force coordinates to the location's center and distance to 0
          const userLat = foundLoc.latitude;
          const userLng = foundLoc.longitude;
          const dist = 0;

          setTempCoords({ lat: userLat, lng: userLng });
          setTempDistance(dist);
          setSelectedLocation(foundLoc);

          // Proceed with same logic as GPS
          let currentConfig = shiftData.todayConfig;
          let currentShiftName = shiftData.todayShift || '';

          if (currentAction === 'masuk') {
              const found = shiftData.activeShifts.find(s => s.name === selectedShift);
              if (found) {
                  currentConfig = found;
                  currentShiftName = selectedShift;
              }
          } else if (currentAction === 'pulang' && activeSession) {
              const found = shiftData.activeShifts.find(s => s.name === activeSession.shift_masuk);
              if (found) {
                  currentConfig = found;
                  currentShiftName = found.name || activeSession.shift_masuk;
              }
          }

          if (!currentConfig) {
              setErrorMessage('Konfigurasi shift tidak ditemukan.');
              setActiveModal('error');
              return;
          }

          const now = new Date();

          if (currentAction === 'masuk') {
              // Duplicate Check-In — hanya untuk HARI INI (zona WIB).
              // dailyRecords juga memuat sesi aktif hingga 3 hari ke belakang
              // (lookback), jadi cek wajib membandingkan tanggal, bukan shift saja.
              const todayWib = getWibDateString(new Date());
              const isDuplicate = dailyRecords.some(r =>
                  r.shift_masuk === currentShiftName &&
                  (!r.tanggal || r.tanggal === todayWib)
              );
              if (isDuplicate) {
                  setErrorMessage("Afwan Presensi Gagal, kamu sudah bekerja dengan baik di shift yg kamu pilih di hari ini, kita bertemu di shift selanjutnya, semoga perkerjaanmu membawa berkah , jazakumullahu khairan");
                  setActiveModal('error');
                  return;
              }

              const result = checkPresensiMasuk(currentConfig, now);

              if (result.status === 'early_masuk') {
                  setErrorMessage(result.message || 'Belum waktunya presensi.');
                  setActiveModal('error');
                  return;
              } else if (result.status === 'late_masuk') {
                  if (authUser?.uid && authUser?.email) {
                      const izin = await diagStep('Cek izin', () => getIzin('terlambat'));

                      if (izin && izin.jam_mulai) {
                          const [izinHour, izinMinute] = izin.jam_mulai.split(':').map(Number);
                          const allowedLateTime = new Date(now);
                          allowedLateTime.setHours(izinHour, izinMinute + 5, 0, 0); // + 5 minutes tolerance

                          if (now <= allowedLateTime) {
                              // Masih dalam batas izin + toleransi
                              handleFinalSubmit('tepat_waktu', null, { lat: userLat, lng: userLng }, dist, currentShiftName, foundLoc.nama);
                              return;
                          }
                      }
                  }
                  setLateConfig(currentConfig);
                  muatMasaPenyesuaian();
                  setActiveModal('late');
              } else if (result.status === 'success' && result.determinedStatus) {
                  handleFinalSubmit(result.determinedStatus as any, null, { lat: userLat, lng: userLng }, dist, currentShiftName, foundLoc.nama);
              }
          } else {
              if (!activeSession) {
                  setErrorMessage('Tidak ada sesi aktif.');
                  setActiveModal('error');
                  return;
              }

              // Pulang Logic
              const result = checkPresensiPulang(currentConfig, shiftData.activeShifts, now, activeSession?.jam_masuk);

              if (result.status === 'overlap_pulang') {
                  setPulangStatus('tidak_presensi_pulang');
                  setActiveModal('missed_checkout');
              } else if (result.status === 'early_pulang') {
                  if (authUser?.uid && authUser?.email) {
                      const izin = await diagStep('Cek izin', () => getIzin('pulang_cepat'));

                      if (izin && izin.jam_mulai) {
                          const [izinHour, izinMinute] = izin.jam_mulai.split(':').map(Number);
                          const allowedEarlyLeaveTime = new Date(now);
                          allowedEarlyLeaveTime.setHours(izinHour, izinMinute, 0, 0);

                          if (now >= allowedEarlyLeaveTime) {
                              // Pulang setelah atau sama dengan jam izin pulang cepat
                              handleFinalSubmit('tepat_waktu', null, { lat: userLat, lng: userLng }, dist, currentShiftName, foundLoc.nama);
                              return;
                          }
                      }
                  }
                  setPulangStatus('pulang_cepat');
                  setActiveModal('early');
              } else if (result.status === 'late_pulang') {
                  setPulangStatus('tidak_presensi_pulang');
                  setActiveModal('missed_checkout');
              } else if (result.status === 'success' && result.determinedStatus) {
                  handleFinalSubmit(result.determinedStatus as any, null, { lat: userLat, lng: userLng }, dist, currentShiftName, foundLoc.nama);
              }
          }

      } catch (err) {
          console.error(err);
          setErrorMessage('Terjadi kesalahan saat memproses data QR.');
          setActiveModal('error');
      }
  };

  const handleShiftSelect = (shiftName: string) => {
      // Check if this shift is already completed OR has a check-in record
      // untuk HARI INI (zona WIB) — record shift kemarin dari lookback sesi
      // aktif TIDAK boleh memblokir presensi masuk hari ini.
      const todayWib = getWibDateString(new Date());
      const existingRecord = dailyRecords.find(r =>
          r.shift_masuk === shiftName &&
          (!r.tanggal || r.tanggal === todayWib)
      );

      if (existingRecord) {
          // Whether completed or just checked in, prevent re-entry.
          // User requested specifically this message for duplicate attempts.
          setErrorMessage("Afwan Presensi Gagal, kamu sudah bekerja dengan baik di shift yg kamu pilih di hari ini, kita bertemu di shift selanjutnya, semoga perkerjaanmu membawa berkah , jazakumullahu khairan");
          setActiveModal('error');
          return;
      }

      setSelectedShift(shiftName);
      getIzin('terlambat', true); // prefetch di latar belakang
      setActiveModal('method');
  };

  const handleLocationSelect = async (loc: LocationData) => {
      if (currentAction === 'pulang' && activeSession && activeSession.lokasi_masuk !== loc.nama) {
          setErrorMessage(`Lokasi pulang harus sama dengan lokasi masuk (${activeSession.lokasi_masuk}).`);
          setActiveModal('error');
          return;
      }

      if (currentAction === 'masuk' && !navigator.geolocation) {
          setErrorMessage('Browser Anda tidak mendukung Geolocation.');
          setActiveModal('error');
          return;
      }

      setSelectedLocation(loc);
      setActiveModal('refresh_lokasi');
  };

  const handleRefreshLokasiConfirm = async (coords: { lat: number; lng: number }, distance: number, _accuracy: number) => {
      startDiag();
      const { lat: userLat, lng: userLng } = coords;
      const dist = distance;
      const loc = selectedLocation;

      if (!loc) {
          setActiveModal('error');
          setErrorMessage('Terjadi kesalahan, lokasi belum dipilih.');
          return;
      }

      setTempCoords({ lat: userLat, lng: userLng });
      setTempDistance(dist);

      // Identify Current Config
      let currentConfig = shiftData.todayConfig; // Fallback
      let currentShiftName = shiftData.todayShift || ''; // Fallback

      if (currentAction === 'masuk') {
          const found = shiftData.activeShifts.find(s => s.name === selectedShift);
          if (found) {
              currentConfig = found;
              currentShiftName = selectedShift;
          }
      } else if (currentAction === 'pulang' && activeSession) {
          const found = shiftData.activeShifts.find(s => s.name === activeSession.shift_masuk);
          if (found) {
              currentConfig = found;
              currentShiftName = found.name || activeSession.shift_masuk;
          }
      }

      if (!currentConfig) {
          setErrorMessage('Konfigurasi shift tidak ditemukan.');
          setActiveModal('error');
          return;
      }

      const now = new Date();

      if (currentAction === 'masuk') {
          // Duplicate Check-In — hanya untuk HARI INI (zona WIB); record dari
          // lookback sesi aktif (hingga 3 hari) tidak memblokir masuk hari ini.
          const todayWib = getWibDateString(new Date());
          const isDuplicate = dailyRecords.some(r =>
              r.shift_masuk === currentShiftName &&
              (!r.tanggal || r.tanggal === todayWib)
          );
          if (isDuplicate) {
              setErrorMessage("Afwan Presensi Gagal, kamu sudah bekerja dengan baik di shift yg kamu pilih di hari ini, kita bertemu di shift selanjutnya, semoga perkerjaanmu membawa berkah , jazakumullahu khairan");
              setActiveModal('error');
              return;
          }
      }

      const [startHour, startMinute] = currentConfig.start.split(':').map(Number);
      const [endHour, endMinute] = currentConfig.end.split(':').map(Number);

      const shiftStart = new Date();
      shiftStart.setHours(startHour, startMinute, 0, 0);

      const shiftEnd = new Date();
      shiftEnd.setHours(endHour, endMinute, 0, 0);

      if (currentAction === 'masuk') {
          const result = checkPresensiMasuk(currentConfig, now);

          if (result.status === 'early_masuk') {
              setErrorMessage(result.message || 'Belum waktunya presensi.');
              setActiveModal('error');
              return;
          } else if (result.status === 'late_masuk') {
              if (authUser?.uid && authUser?.email) {
                  const izin = await diagStep('Cek izin', () => getIzin('terlambat'));

                  if (izin && izin.jam_mulai) {
                      const [izinHour, izinMinute] = izin.jam_mulai.split(':').map(Number);
                      const allowedLateTime = new Date(now);
                      allowedLateTime.setHours(izinHour, izinMinute + 5, 0, 0); // + 5 minutes tolerance

                      if (now <= allowedLateTime) {
                          // Masih dalam batas izin + toleransi
                          handleFinalSubmit('tepat_waktu', null, { lat: userLat, lng: userLng }, dist, currentShiftName, loc.nama);
                          return;
                      }
                  }
              }
              setLateConfig(currentConfig);
              muatMasaPenyesuaian();
              setActiveModal('late');
          } else if (result.status === 'success' && result.determinedStatus) {
              handleFinalSubmit(result.determinedStatus as any, null, { lat: userLat, lng: userLng }, dist, currentShiftName, loc.nama);
          }
      } else {
          const result = checkPresensiPulang(currentConfig, shiftData.activeShifts, now, activeSession?.jam_masuk);

          if (result.status === 'overlap_pulang') {
              setPulangStatus('tidak_presensi_pulang');
              setActiveModal('missed_checkout');
          } else if (result.status === 'early_pulang') {
              if (authUser?.uid && authUser?.email) {
                  const izin = await diagStep('Cek izin', () => getIzin('pulang_cepat'));

                  if (izin && izin.jam_mulai) {
                      const [izinHour, izinMinute] = izin.jam_mulai.split(':').map(Number);
                      const allowedEarlyLeaveTime = new Date(now);
                      allowedEarlyLeaveTime.setHours(izinHour, izinMinute, 0, 0);

                      if (now >= allowedEarlyLeaveTime) {
                          // Pulang setelah atau sama dengan jam izin pulang cepat
                          handleFinalSubmit('tepat_waktu', null, { lat: userLat, lng: userLng }, dist, currentShiftName, loc.nama);
                          return;
                      }
                  }
              }
              setPulangStatus('pulang_cepat');
              setActiveModal('early');
          } else if (result.status === 'late_pulang') {
              setPulangStatus('tidak_presensi_pulang');
              setActiveModal('missed_checkout');
          } else if (result.status === 'success' && result.determinedStatus) {
              handleFinalSubmit(result.determinedStatus as any, null, { lat: userLat, lng: userLng }, dist, currentShiftName, loc.nama);
          }
      }
  };

  const handleLateSubmit = (reason: string, udzur?: UdzurMasuk) => {
      const shiftName = currentAction === 'pulang' && activeSession ? activeSession.shift_masuk : selectedShift || shiftData.todayConfig?.name || '';
      if (selectedLocation && tempCoords && shiftName) {
          if (udzur && lateConfig) {
              // Udzur masa penyesuaian: tepat waktu, jam masuk = jam akhir toleransi.
              const jamMasuk = getJamAkhirToleransi(lateConfig, new Date());
              handleFinalSubmit('tepat_waktu', reason, tempCoords, tempDistance, shiftName, selectedLocation.nama, { udzur, jamMasuk });
              return;
          }
          handleFinalSubmit('terlambat', reason, tempCoords, tempDistance, shiftName, selectedLocation.nama);
      }
  };

  // Info udzur untuk modal terlambat (null = masa penyesuaian tidak berlaku).
  const udzurModal = useMemo(() => {
      if (!masaPenyesuaian || !lateConfig || activeModal !== 'late') return null;
      const jamAkhir = getJamAkhirToleransi(lateConfig, new Date());
      return {
          jamToleransi: jamAkhir.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }),
          dalamBatas: isDalamBatasUdzur(jamAkhir, new Date()),
      };
  }, [masaPenyesuaian, lateConfig, activeModal]);

  const handleEarlySubmit = (reason: string) => {
      const shiftName = currentAction === 'pulang' && activeSession ? activeSession.shift_masuk : selectedShift || shiftData.todayConfig?.name || '';
      if (selectedLocation && tempCoords && shiftName) {
          // Pass the determined status (pulang_cepat)
          handleFinalSubmit(pulangStatus, reason, tempCoords, tempDistance, shiftName, selectedLocation.nama);
      }
  };

  const handleMissedCheckoutSubmit = (reason: string) => {
      if (pendingMissedCheckout) {
          // Route through the main handleFinalSubmit which we already patched to handle pendingMissedCheckout
          handleFinalSubmit('tidak_presensi_pulang', reason, { lat: 0, lng: 0 }, 0, pendingMissedCheckout.shiftName, 'Sistem');
          return;
      }

      const shiftName = currentAction === 'pulang' && activeSession ? activeSession.shift_masuk : selectedShift || shiftData.todayConfig?.name || '';

      // If tempCoords/selectedLocation are missing (e.g. from forced auto-checkout modal), supply dummy data
      const finalCoords = tempCoords || { lat: 0, lng: 0 };
      const finalDist = tempDistance || 0;
      const finalLoc = selectedLocation?.nama || activeSession?.lokasi_masuk || 'Sistem';

      if (shiftName) {
          handleFinalSubmit('tidak_presensi_pulang', reason, finalCoords, finalDist, shiftName, finalLoc);
      }
  };

  const handleFinalSubmit = async (
      status: 'tepat_waktu' | 'terlambat' | 'pulang_cepat' | 'lembur' | 'tidak_presensi_pulang',
      reason: string | null,
      coords: { lat: number; lng: number },
      distance: number,
      shiftName: string,
      locationName: string,
      udzurMasuk?: { udzur: UdzurMasuk; jamMasuk: Date }
  ) => {
      // Guard klik ganda: modal loading dipasang, tapi di jaringan lambat user
      // bisa menekan submit dua kali sebelum re-render selesai.
      if (presensiSubmitting || activeModal === 'loading' || submitInFlightRef.current) return;
      submitInFlightRef.current = true;

      setActiveModal('loading');
      if (!isDiagActive()) startDiag();
      // If we are submitting a missed checkout reason, we don't call submitAttendance again because the background task already closed it.
      // Instead, we just update the specific record's reason.
      let result: { success: boolean; message?: string } = { success: false, message: '' };

      if (pendingMissedCheckout) {
         // Submitting the reason for a previously auto-closed session
         try {
             const { supabase } = await import('../../lib/supabase');
             const { error } = await supabase
                .from('presensi_pegawai')
                .update({ alasan_pulang: reason })
                // user_id in presensi_pegawai is populated by userId from useAuth via usePresensiPegawai
                .eq('user_id', authUser?.uid || '')
                .eq('shift_masuk', pendingMissedCheckout.shiftName)
                .eq('status_pulang', 'tidak_presensi_pulang')
                // Let's rely on checking the `jam_masuk` as well if we stored it
                .eq('jam_masuk', pendingMissedCheckout.jam_masuk);

             if (error) throw error;

             result = { success: true, message: 'Reason saved' };
             window.dispatchEvent(new Event('presensi_updated'));
             setPendingMissedCheckout(null); // Clear the block
         } catch (e: any) {
             result = { success: false, message: e.message };
         }
      } else {
         result = await submitAttendance(currentAction, shiftName, locationName, status, reason, coords, distance, activeSession, undefined, udzurMasuk);
      }

      endDiag();

      if (result.success) {
          // Set state lokal TIDAK diperlukan lagi — `presensi_updated` yang
          // dipancarkan oleh usePresensiSubmit akan memicu refetch hari ini.
          setSuccessData({ type: currentAction, status });
          setActiveModal('success');
      } else {
          setErrorMessage(result.message || 'Gagal menyimpan presensi. Silakan coba lagi.');
          setActiveModal('error');
      }

      // Reset guard HANYA setelah modal berikutnya dipasang, supaya tidak ada
      // jendela antara re-render di mana klik kedua bisa lolos.
      submitInFlightRef.current = false;
  };

  const closeAll = () => {
      if (isDiagActive()) finishDiag(); // proses dibatalkan di tengah jalan
      setDiagSummary('');
      setActiveModal(null);
      setErrorMessage('');
  };

  // Determine Button State
  // Red (Check In) if no active session. Amber (Check Out) if active session.
  const isCheckedIn = !!activeSession;

  return (
    <>
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-3 pb-6 flex justify-between items-center z-50">
        <NavButton
            icon={<Home size={24} />}
            label="Beranda"
            active={activeTab === 'home'}
            onClick={() => navigate('/portal-guru')}
        />
        <NavButton
            icon={<Newspaper size={24} />}
            label="Berita"
            active={activeTab === 'berita'}
            onClick={() => navigate('/portal-guru/berita')}
        />

        {!isAkunLayanan && <div className="relative w-14 h-10 flex justify-center items-center pointer-events-none">
            <button
                onClick={handleLocationClick}
                disabled={!isAppReady}
                className={`absolute -top-8 pointer-events-auto rounded-full size-14 flex items-center justify-center shadow-lg ring-[6px] ring-white transform transition-transform ${
                    isAppReady ? 'hover:scale-105 active:scale-95' : 'opacity-70 cursor-not-allowed'
                } ${
                    !isAppReady 
                        ? 'bg-gray-400 text-white shadow-gray-400/30'
                        : isCheckedIn
                            ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/30'
                            : 'bg-red-600 hover:bg-red-700 text-white shadow-red-600/30'
                }`}
            >
                {!isAppReady ? (
                    <Loader2 size={28} className="animate-spin" />
                ) : isCheckedIn ? (
                    <ArrowRight size={28} />
                ) : (
                    <MapPin size={28} />
                )}
            </button>
        </div>}

        {!isAkunLayanan && <NavButton
            icon={<BarChart3 size={24} />}
            label="Kinerja"
            active={activeTab === 'kinerja'}
            onClick={() => navigate('/portal-guru/kinerja')}
        />}
        <NavButton
            icon={<User size={24} />}
            label="Profil"
            active={activeTab === 'profil'}
            onClick={() => navigate('/portal-guru/profil')}
        />
        </div>

        {/* Modals */}
        {activeModal === 'confirm_pulang' && (
            <PortalGuruModalKonfirmasiPulang
                onConfirm={handleConfirmPulang}
                onCancel={closeAll}
            />
        )}

        {activeModal === 'shift' && shiftData.activeShifts && (
            <PortalGuruModalPilihanShift
                shiftTime={shiftData.todayShift || undefined}
                availableShifts={shiftData.activeShifts}
                onSelect={handleShiftSelect}
                onClose={closeAll}
            />
        )}

        {activeModal === 'auto_closed_info' && (
            <PortalGuruModalAutoClosedInfo
                shiftName={selectedShift || autoClosedSessionInfo?.shiftName || 'Shift'}
                onClose={() => {
                    clearAutoClosedSessionInfo();
                    closeAll();
                }}
            />
        )}

        {activeModal === 'method' && (
            <PortalGuruModalMetodePresensi
                onSelect={handleMethodSelect}
                onClose={closeAll}
            />
        )}

        {activeModal === 'scanner' && (
            <PortalGuruScannerPresensi
                onSuccess={handleQRSuccess}
                onClose={closeAll}
            />
        )}


        {activeModal === 'refresh_lokasi' && selectedLocation && (
            <PortalGuruModalRefreshLokasi
                targetName={selectedLocation.nama}
                targetLat={selectedLocation.latitude}
                targetLng={selectedLocation.longitude}
                radius={selectedLocation.radius || 50}
                geofenceType={selectedLocation.geofence_type}
                polygonCoordinates={selectedLocation.polygon_coordinates}
                calculateDistance={calculateDistance}
                isPointInPolygon={isPointInPolygon}
                onConfirm={handleRefreshLokasiConfirm}
                onCancel={() => setActiveModal('location')}
            />
        )}

        {activeModal === 'location' && (
            <PortalGuruModalPilihanLokasi
                onSelect={handleLocationSelect}
                onClose={closeAll}
            />
        )}

        {activeModal === 'success' && successData && (
            <PortalGuruModalPresensiTepat
                type={successData.type}
                status={successData.status}
                onClose={closeAll}
                diagnostic={diagSummary}
            />
        )}

        {activeModal === 'shift_completed' && (
            <PortalGuruModalShiftSelesai
                namaUser={authUser?.displayName || 'Ustadz/Ustadzah'}
                onClose={closeAll}
            />
        )}

        {activeModal === 'late' && (
            <PortalGuruModalPresensiTerlambat onSubmit={handleLateSubmit} onClose={closeAll} udzur={udzurModal} />
        )}

        {activeModal === 'early' && (
            <PortalGuruModalPulangCepat onSubmit={handleEarlySubmit} onClose={closeAll} />
        )}

        {activeModal === 'missed_checkout' && (
            <PortalGuruModalTidakPresensiPulang onSubmit={handleMissedCheckoutSubmit} onClose={closeAll} />
        )}

        {activeModal === 'error' && (
            <PortalGuruModalError message={errorMessage} onClose={closeAll} diagnostic={diagSummary} />
        )}

        {activeModal === 'loading' && (
            <div className="fixed inset-0 z-[120] flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm">
                <Loader2 className="animate-spin text-white" size={48} />
                <p className="text-white/90 text-sm font-medium mt-4 px-6 text-center">
                    Menyimpan presensi Anda{'\u2026'}<br />
                    <span className="text-white/60 text-xs font-normal">Mohon tunggu sebentar, jangan tutup aplikasi.</span>
                </p>
                <p className="text-white/50 text-[11px] mt-3 font-mono">
                    {diagLabel ? `${diagLabel}… ` : ''}{(diagElapsed / 1000).toFixed(0)} dtk
                </p>
            </div>
        )}
    </>
  );
};

const NavButton: React.FC<{ icon: React.ReactNode, label: string, active?: boolean, onClick?: () => void }> = ({ icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center justify-center gap-1 w-16 h-14 rounded-2xl transition-all duration-300 ${
      active
        ? 'text-[#40513B]'
        : 'text-gray-400 hover:text-[#40513B]'
    }`}
  >
    {icon}
    <span className="text-[10px] font-medium">{label}</span>
  </button>
);
